<template>
  <DefaultLayout>
  <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
    {{ loadingAnnouncement }}
  </div>
  <div id="product-plp-layout" class="font-sans text-gray-900 bg-[#FBFBFB]">
        <div class="m-auto grid grid-cols-1 py-6 bg-[#FBFBFB] max-w-[var(--max-w-container)] xl:max-w-[var(--max-w-container-xl)] 2xl:max-w-[var(--max-w-container-2xl)]">
          <div class="lg:col-span-3 block">
            <AgenticResults
              :response="agenticResponse"
              :initial-filter="routeAgenticQuery.filterKey"
              :selected-filters="appliedFilters"
              :is-filtering="isFiltering"
              :isPlpReady="isPlpReady"
              :showProductGrid="showProductGrid"
              :showContent="showContent"
              @select-filter="onSelectFilter"
              @revert-filter="onRevertFilter"
              @compare-products="onCompareProducts"
            />
          </div>
        </div>
    </div>

    <AgenticComparisonModal
      :is-open="isCompareModalOpen"
      :is-loading="isComparing"
      :comparison-data="hasCompareResponse ? compareResponse : null"
      :adding-product-sku="addingComparisonProductSku"
      @close="closeComparisonModal"
      @add-to-cart="handleComparisonAddToCart"
    />
  </DefaultLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import DefaultLayout from '~/layouts/default.vue'
import Error from '~/components/ui/atom/error.vue'
import AgenticResults from '~/components/ui/organism/product/agentic/agenticResults.vue'
import AgenticComparisonModal from '~/components/ui/organism/product/agentic/agenticComparisonModal.vue'
import { URL_CONFIG } from '~~/shared/utils/url.const'
import { PRODUCT_CONTENT } from '~~/shared/utils/contentTypes'
import { CATEGORIES } from '~/constants/categories'
import { useScrollToTop } from '~/composables/useScrollToTop'
import { useProducts } from '~/composables/useProducts'
import { useAgenticProductQuery } from '~/composables/useAgenticProductQuery'
import siteService from '~/composables/siteService'
import eventBusGtm from '~/composables/eventBusGtm';
import { useUserStore } from "@/stores/userStore";
import { useCartModalStore } from '@/stores/cartModalStore'
import { useGtmStore } from '~/stores/googleTagManagerStore'
import { useIframeStore } from '~/stores/chatAIStore'
import { storeToRefs } from 'pinia'
import { convertPriceStringToFloat } from '~~/shared/utils/price'
import type { AddToCartAnalyticsContext, AgenticProduct } from '~~/shared/types/common'
const iframeStore = useIframeStore()
const { isDynamicYieldVariationData } = storeToRefs(iframeStore);
const userStore = useUserStore();
const cartModalStore = useCartModalStore()

const route = useRoute()
const router = useRouter()
const breakPoints = useBreakpoints(breakpointsTailwind);
const isMobileBreakpoint = breakPoints.smaller('md');

const productsComposable = useProducts()
const initialAgenticFilterQuery = route.query.filterkey

const normalizeAgenticFilterValue = (value: string) => {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' ')).trim()
  } catch {
    return value.replace(/\+/g, ' ').trim()
  }
}

const normalizeAgenticFilters = (filters: string[]) => {
  return [...new Set(filters
    .flatMap(filter => filter.split(','))
    .map(normalizeAgenticFilterValue)
    .filter(Boolean))]
}

const getQueryString = (value: unknown) => {
  const normalized = Array.isArray(value) ? value[0] : value
  return typeof normalized === 'string' ? normalizeAgenticFilterValue(normalized) : ''
}

const getQueryStringList = (value: unknown) => {
  const values = Array.isArray(value) ? value : [value]
  return normalizeAgenticFilters(values
    .flatMap(item => typeof item === 'string' ? item.split(',') : [])
  )
}

const getIndexedFilterQuery = (query: Record<string, unknown>) => {
  return normalizeAgenticFilters(Object.entries(query)
    .filter(([key]) => /^f\[\d+\]$/.test(key))
    .sort(([first], [second]) => Number(first.slice(2, -1)) - Number(second.slice(2, -1)))
    .flatMap(([, value]) => Array.isArray(value) ? value : [value])
    .filter((value): value is string => typeof value === 'string'))
}

const resolveAgenticQuery = () => {
  const filterKey = getQueryString(initialAgenticFilterQuery)
  const appliedFilterKeys = Object.keys(route.query).some(key => /^f\[\d+\]$/.test(key))
    ? getIndexedFilterQuery(route.query)
    : getQueryStringList(route.query.agenticfilters)
  const filterId = getQueryString(route.query.filter_id)
  const filterState = getQueryString(route.query.filter_state)

  return {
    filterKey,
    appliedFilterKeys,
    filterId,
    filterState
  }
}

const routeAgenticQuery = resolveAgenticQuery()

const {
  response,
  compareResponse,
  appliedFilters,
  isFiltering,
  isComparing,
  hasQueryResponse,
  hasCompareResponse,
  setInitContext,
  addFilter,
  revertFilter,
  runCompareQuery
} = useAgenticProductQuery(
  routeAgenticQuery.filterId,
  routeAgenticQuery.filterState,
  routeAgenticQuery.filterKey,
  routeAgenticQuery.appliedFilterKeys
)

const agenticResponse = response

const updateAgenticFiltersInUrl = () => {
  if (!process.client) return

  const filters = normalizeAgenticFilters(appliedFilters.value)
  const nextQuery = { ...route.query }

  if (initialAgenticFilterQuery) {
    nextQuery.filterkey = initialAgenticFilterQuery
  } else {
    delete nextQuery.filterkey
  }

  router.replace({ path: route.path, query: nextQuery })
}

const onSelectFilter = async (filterKey: string) => {
  try {
    console.log(filterKey,'filterKey select')
    await addFilter(filterKey)
    updateAgenticFiltersInUrl()

    const customerId = userStore.data?.customerId ?? null;
    eventBusGtm.emit("trackEvent", {
      event_name: "Agenticplp",
      button_name: "click_filter",
      screen_name: "Agenticplp_results",
      Customer_id: customerId,
      filter_name: filterKey,
      message_id: null,
      Selected_thread_id: null,
      target: null,
      productName: null,
      productSku: null,
      productPrice: null,
      skuid: null,
      sub_screen: null,
      article: null,
      Writing_style_preference: null,
      Content_preference: null,
      ResponseDetail_preference: null,
      Taia_campaign: null,
      search_term: null,
      variationName: isDynamicYieldVariationData.value?.name ?? null,
      experienceName: isDynamicYieldVariationData.value?.experienceName ?? null,
    });

  } catch {
    // Error surface follows existing toast-based pattern.
  }
}

const onRevertFilter = async (filterKey?: string) => {
  try {
    await revertFilter(filterKey)
     const customerId = userStore.data?.customerId ?? null;
    eventBusGtm.emit("trackEvent", {
      event_name: "Agenticplp",
      button_name: "click_breadcrumb",
      screen_name: "Agenticplp_results",
      Customer_id: customerId,
      filter_name: filterKey,
      message_id: null,
      Selected_thread_id: null,
      target: null,
      productName: null,
      productSku: null,
      productPrice: null,
      skuid: null,
      sub_screen: null,
      article: null,
      Writing_style_preference: null,
      Content_preference: null,
      ResponseDetail_preference: null,
      Taia_campaign: null,
      search_term: null,
      variationName: isDynamicYieldVariationData.value?.name ?? null,
      experienceName: isDynamicYieldVariationData.value?.experienceName ?? null,
    });
    updateAgenticFiltersInUrl()
  } catch {
    // Error surface follows existing toast-based pattern.
  }
}

const onCompareProducts = () => {
  openComparisonModal()
}
const isCompareModalOpen = ref(false)
const addingComparisonProductSku = ref<string | null>(null)

const closeComparisonModal = () => {
  isCompareModalOpen.value = false
}

const openComparisonModal = async () => {
  if (!agenticResponse.value.products.length) {
    return
  }

  isCompareModalOpen.value = true

  eventBusGtm.emit('trackEvent', {
    event_name: 'Agenticplp',
    button_name: 'Compare_these',
    screen_name: 'Agenticplp_results',
    Customer_id: userStore.data?.customerId ?? null,
  })
  
  const compareSkus = (agenticResponse.value.products || [])
    .map(product => product?.sku)
    .filter((sku): sku is string => typeof sku === 'string' && sku.trim().length > 0)

  try {
    const customerId = userStore.data?.customerId != null ? String(userStore.data.customerId) : ''
    await runCompareQuery(customerId, compareSkus)
  } catch {}
}

const getComparisonAnalyticsContext = (product: AgenticProduct): AddToCartAnalyticsContext => {
  const price = convertPriceStringToFloat(product?.price)

  return {
    screen_name: 'Agenticplp_results',
    sub_screen: 'Compare_products',
    item_id: product?.sku,
    item_name: product?.name ?? product?.productName,
    item_category: product?.searchTag ?? product?.benefits?.[0],
    price: Number.isFinite(price) ? price : undefined,
    item_list_id: 'agentic_compare_results',
    item_list_name: 'Agentic Compare Results'
  }
}

const trackComparisonAddToCart = (product: AgenticProduct) => {
  const productPrice = product.price ?? product.salePrice ?? product.retailPrice ?? product.wholesalePrice ?? null

  eventBusGtm.emit('trackEvent', {
    event_name: 'Agenticplp',
    button_name: 'add_to_cart',
    screen_name: 'Compare_modal',
    Customer_id: userStore.data?.customerId ?? null,
    message_id: null,
    Selected_thread_id: null,
    target: null,
    productName: product.name ?? product.productName ?? null,
    productSku: product.sku ?? null,
    productPrice,
    skuid: null,
    sub_screen: null,
    article: null,
    Writing_style_preference: null,
    Content_preference: null,
    ResponseDetail_preference: null,
    Taia_campaign: null,
    search_term: null,
    variationName: isDynamicYieldVariationData.value?.name ?? null,
    experienceName: isDynamicYieldVariationData.value?.experienceName ?? null,
  })
}

const handleComparisonAddToCart = async (product: AgenticProduct) => {
  if (addingComparisonProductSku.value) return

  if (!product?.id) {
    cartModalStore.open()
    return
  }

  addingComparisonProductSku.value = product.sku

  try {
    const analyticsContext = getComparisonAnalyticsContext(product)
    const response = await siteService().addToCart(product.id, 1, 0, false, analyticsContext)
    const lines = response.lines ?? []
    const modalData = lines.find(line => line.productId === product.id)
    const totalQuantity = lines.reduce((sum, line) => sum + line.quantity, 0)

    useGtmStore().selectProductEvent(analyticsContext)
  trackComparisonAddToCart(product)

    cartModalStore.open(
      {
        name: modalData?.name || product.name,
        subtotal: modalData?.subtotal,
        imageUrl: product.primaryImageUrl,
        quantity: modalData?.quantity || 1,
        id: `add-to-cart-${product.id}`
      },
      totalQuantity
    )
  } catch {
    cartModalStore.open()
  } finally {
    addingComparisonProductSku.value = null
  }
}

const {
  loading,
  error,
  initFromUrl,
  isCheckproductData,
  initialized
} = productsComposable


const hasTriggeredInitialAgenticQuery = ref(false)

const triggerInitialAgenticQuery = async () => {
  if (hasTriggeredInitialAgenticQuery.value) return

  hasTriggeredInitialAgenticQuery.value = true
  try {
    if (!routeAgenticQuery.filterKey) {
      console.warn('[agenticProducts] missing filterkey query param; landing query API was skipped')
      hasTriggeredInitialAgenticQuery.value = false
      return
    }

    await addFilter()
  } catch {
    hasTriggeredInitialAgenticQuery.value = false
  }
}

const isPlpInitialized = ref(false)
const shouldRunInitialAgenticQuery = computed(() => !!routeAgenticQuery.filterKey)
const isPlpReady = computed(() => (
  isPlpInitialized.value &&
  !isFiltering.value &&
  (!shouldRunInitialAgenticQuery.value || hasQueryResponse.value)
))

const loadingMessage = 'We’re grabbing your products. Hold on a few seconds while we retrieve your products.'
const loadingAnnouncement = ref('')
watch(
  isPlpReady,
  (isReady) => {
    if (isReady) {
      loadingAnnouncement.value = ''
    } else {
      nextTick(() => {
        loadingAnnouncement.value = loadingMessage
      })
    }
  },
  { immediate: true }
)

const storeProductInfo = () => {
  const products = agenticResponse.value.products ?? []
  if (process.client) {
    const structuredProducts = products?.map(product => ({
      id: product?.id ?? null,
      sku: product?.sku ?? '',
      productName: product?.name ?? product?.productName ?? '',
      substituteProductIds: product?.substituteProductIds ?? [],
      benefits: product?.benefits ?? [],
    }))
    localStorage.setItem('plpProducts', JSON.stringify(structuredProducts))
  }
}

initFromUrl().then(async () => {
  isPlpInitialized.value = true
  storeProductInfo()
  await triggerInitialAgenticQuery()
})

const showContent = ref(false)
watch(loading, (val) => {
  if (val) {
    showContent.value = false
  } else {
    showContent.value = true
  }
}, { immediate: true })

const hasProductListData = computed(() => {
  const data = agenticResponse.value.products
  return Array.isArray(data) && data.length > 0
})

const showProductGrid = computed(
  () => loading.value || hasProductListData.value
)

const currentPage = ref(1)

const getPageFromRoute = () => {
  const pageParam = route.query.page
  if (!pageParam) return 1

  const raw = Array.isArray(pageParam) ? pageParam[0] : pageParam
  const parsed = Number.parseInt(String(raw), 10)

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
}

currentPage.value = getPageFromRoute()

watch(
  () => route.query.page,
  () => {
    currentPage.value = getPageFromRoute()
  }
)

watch(
  () => agenticResponse.value.products,
  () => {
    storeProductInfo()
  },
  { immediate: true }
)

useScrollToTop(currentPage)

const hideFilter = computed(() => {
  const segments = route.path.split('/').filter(Boolean)
  return segments.slice(-2).join('/') === URL_CONFIG.SET_BUNDLES
})


const selectedCategoryMetaTitle = computed(() => {
  const slug = route.params.slug
  if (!slug) return PRODUCT_CONTENT.SUPPLEMENTS_TESTS

  const path = Array.isArray(slug) ? slug.join('/') : slug
  const last = Array.isArray(slug) ? slug.at(-1) : slug

  const match =
    CATEGORIES.find(cat => cat.urlKey === path) ||
    CATEGORIES.find(cat => cat.urlKey.endsWith(`/${last}`))

  return match?.metaTitle ?? PRODUCT_CONTENT.SUPPLEMENTS_TESTS
})

const headerMetaTitle = computed(() =>
  route.path.startsWith('/tests') ? 'At-Home Tests & Health Panels' : selectedCategoryMetaTitle.value
)

useHead(() => ({
  title: `${headerMetaTitle.value} | Thorne`
}))

</script>
